import { useEffect, useState } from "react";
import ActionButton from "@/components/ActionButton";
import api, { formatDate, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Sparkles, Loader2, Star, Link2, Wand2, CheckCircle2, Trash2, ChevronRight, GripVertical, ImagePlus, X, Image as ImageIcon, Linkedin, Send, Clock, AlertTriangle, ThumbsUp, MessageSquare, Repeat2, Globe } from "lucide-react";
import MiniCalendar, { CalendarKpis } from "@/components/MiniCalendar";

const COLS = [
  { id: "idee", label: "Idée" },
  { id: "a_developper", label: "À développer" },
  { id: "brouillon", label: "Brouillon" },
  { id: "a_relire", label: "À relire" },
  { id: "valide", label: "Validé" },
  { id: "a_publier", label: "Programmé" },
  { id: "publie", label: "Publié" },
];

const TAGS = ["franchise", "capex", "rentabilite", "standardisation", "sourcing", "chantier", "retail", "restauration"];

const PUB_TYPES = {
  PPD: [
    ["retour_experience", "Retour d'expérience chantier"],
    ["franchise", "Franchise"],
    ["concept", "Création de concept"],
    ["standardisation", "Standardisation"],
    ["capex", "CAPEX"],
    ["rentabilite", "Rentabilité"],
    ["erreurs", "Erreurs fréquentes"],
    ["coulisses", "Coulisses agence"],
  ],
  FRANCHIMOB: [
    ["sourcing", "Sourcing"],
    ["achats", "Optimisation achats"],
    ["mutualisation", "Mutualisation réseau"],
    ["fournisseurs", "Fournisseurs"],
    ["gains", "Gains obtenus"],
    ["deploiement", "Déploiement franchisés"],
    ["nouveautes", "Nouveautés produits"],
  ],
};

function Stars({ value, onChange, size = 14 }) {
  const interactive = typeof onChange === "function";
  if (!interactive) {
    return (
      <span className="inline-flex items-center gap-0.5">
        {[1, 2, 3].map((n) => (
          <Star key={n} size={size} className={n <= value ? "fill-amber-400 text-amber-400" : "text-gray-300"} />
        ))}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3].map((n) => (
        <button key={n} type="button" data-testid={`star-${n}`} onClick={() => onChange(n === value ? 0 : n)} className="hover:scale-110 transition">
          <Star size={size} className={n <= value ? "fill-amber-400 text-amber-400" : "text-gray-300"} />
        </button>
      ))}
    </div>
  );
}

function WeekSlots() { return null; /* deprecated — replaced by MiniCalendar */ }

function CalendarKpisWrapper() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = async () => { const r = await api.get("/ideas/calendar/month"); setData(r.data); };
    load();
    const refresh = () => load();
    window.addEventListener("ideas:refresh", refresh);
    return () => window.removeEventListener("ideas:refresh", refresh);
  }, []);
  return <CalendarKpis data={data}/>;
}

export default function Ideas() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [suggesting, setSuggesting] = useState(null); // 'PPD' | 'FRANCHIMOB' | null
  const [suggestions, setSuggestions] = useState(null);
  const [filterTag, setFilterTag] = useState("");
  const [formTags, setFormTags] = useState([]);
  const [formPotentiel, setFormPotentiel] = useState(2);
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [captureText, setCaptureText] = useState("");
  const [analyzingImg, setAnalyzingImg] = useState(false);

  const analyzeImageForPrompt = async (file) => {
    if (!file) return;
    setAnalyzingImg(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result);
        rd.onerror = rej;
        rd.readAsDataURL(file);
      });
      const brand = document.getElementById("form-brand")?.value || "PPD";
      const { data } = await api.post("/ideas/analyze-image", { image_base64: dataUrl, brand });
      setCaptureText(data.capture || "");
      toast.success("Idée générée depuis l'image — ajustez si besoin");
    } catch (e) { /* toast via interceptor */ } finally { setAnalyzingImg(false); }
  };
  const [li, setLi] = useState(null); // statut connexion LinkedIn
  const [publishing, setPublishing] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [liText, setLiText] = useState("");
  const [liPreviewExpanded, setLiPreviewExpanded] = useState(false);

  const loadLi = async () => {
    try { const { data } = await api.get("/linkedin/status"); setLi(data); } catch { setLi(null); }
  };

  const load = async () => {
    const [i, p, c] = await Promise.all([
      api.get("/ideas"),
      api.get("/projects"),
      api.get("/ideas/calendar"),
    ]);
    setList(i.data); setProjects(p.data); setCalendar(c.data);
  };
  useEffect(() => { load(); loadLi(); }, []);

  // Retour OAuth LinkedIn (?linkedin=connected|error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const s = p.get("linkedin");
    if (s === "connected") { alert("Compte LinkedIn connecté ✓"); loadLi(); }
    else if (s === "error") { alert("Échec connexion LinkedIn : " + (p.get("msg") || "inconnu")); }
    if (s) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  // Pré-remplit le texte du post quand on ouvre une idée
  useEffect(() => {
    if (detail) setLiText(detail.brouillon || detail.brouillon_court || detail.capture || detail.title || "");
    setScheduleAt("");
    setLiPreviewExpanded(false);
  }, [detail?.id]); // eslint-disable-line

  const connectLinkedIn = async () => {
    try { const { data } = await api.get("/linkedin/auth-url"); window.location.href = data.url; }
    catch (e) { alert("Erreur : " + (e.response?.data?.detail || e.message)); }
  };
  const disconnectLinkedIn = async () => {
    if (!confirm("Déconnecter le compte LinkedIn ?")) return;
    await api.post("/linkedin/disconnect"); loadLi();
  };

  const publishToLinkedIn = async (schedule) => {
    if (!detail) return;
    if (!li?.connected) { alert("Connecte d'abord ton compte LinkedIn (bouton en haut à droite)."); return; }
    if (schedule && !scheduleAt) { alert("Choisis une date/heure de programmation."); return; }
    setPublishing(true);
    try {
      const body = { text: liText };
      if (schedule) body.schedule_at = scheduleAt;
      const { data } = await api.post(`/linkedin/ideas/${detail.id}/publish`, body);
      alert(data.status === "scheduled" ? "Publication programmée ✓" : "Publié sur LinkedIn ✓");
      load(); setDetail(null);
    } catch (e) {
      alert("Erreur publication : " + (e.response?.data?.detail || e.message));
    } finally { setPublishing(false); }
  };

  const cancelSchedule = async () => {
    const { data } = await api.post(`/linkedin/ideas/${detail.id}/cancel-schedule`);
    setDetail(data); load();
  };

  const create = async (e) => {
    e.preventDefault();
    const form = e.currentTarget.closest("form");
    const fd = Object.fromEntries(new FormData(form).entries());
    const proj = projects.find((p) => p.id === fd.project_id);
    if (proj) { fd.project_nom = proj.nom; fd.client = proj.client; }
    fd.tags = formTags;
    fd.potentiel = formPotentiel;
    await api.post("/ideas", fd);
    setShow(false);
    setFormTags([]); setFormPotentiel(2); setCaptureText("");
    load();
  };

  const setStatus = async (id, status) => {
    await api.put(`/ideas/${id}`, { status });
    load();
    if (detail?.id === id) setDetail({ ...detail, status });
  };

  const setPotentiel = async (id, potentiel) => {
    await api.put(`/ideas/${id}`, { potentiel });
    if (detail?.id === id) setDetail({ ...detail, potentiel });
    load();
  };

  const setScheduledDate = async (id, date) => {
    await api.put(`/ideas/${id}`, { scheduled_date: date });
    if (detail?.id === id) setDetail({ ...detail, scheduled_date: date });
    load();
  };

  // Drag & drop entre colonnes (style Trello) → met à jour le statut
  const handleDrop = async (status) => {
    const id = dragId;
    setDragId(null);
    setDragOverCol(null);
    if (!id) return;
    const idea = list.find((x) => x.id === id);
    if (!idea || idea.status === status) return;
    const patch = { status };
    if (status === "publie" && !idea.scheduled_date) patch.scheduled_date = new Date().toISOString().slice(0, 10);
    await api.put(`/ideas/${id}`, patch);
    if (detail?.id === id) setDetail({ ...detail, ...patch });
    load();
  };

  // Case "Publié" : passe en colonne Publié (date du jour auto, modifiable) ou revient à Programmé
  const togglePublish = async (idea) => {
    if (idea.status === "publie") {
      await api.put(`/ideas/${idea.id}`, { status: "a_publier" });
    } else {
      const patch = { status: "publie" };
      if (!idea.scheduled_date) patch.scheduled_date = new Date().toISOString().slice(0, 10);
      await api.put(`/ideas/${idea.id}`, patch);
    }
    if (detail?.id === idea.id) load();
    load();
  };

  const deleteIdea = async (id, e) => {
    if (e) e.stopPropagation();
    if (!confirm("Supprimer définitivement cette idée ?")) return;
    try {
      await api.delete(`/ideas/${id}`);
      toast.success("Idée supprimée");
      if (detail?.id === id) setDetail(null);
      load();
    } catch (err) { /* toast via interceptor */ }
  };

  const uploadImages = async (files) => {
    if (!detail || !files?.length) return;
    setUploadingImg(true);
    try {
      let updated = detail;
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/ideas/${detail.id}/images`, fd);
        updated = data;
      }
      setDetail(updated);
      load();
    } catch (e) {
      alert("Erreur upload image: " + (e.response?.data?.detail || e.message));
    } finally { setUploadingImg(false); }
  };

  const deleteImage = async (imgId) => {
    const { data } = await api.delete(`/ideas/${detail.id}/images/${imgId}`);
    setDetail(data);
    load();
  };

  const generate = async () => {
    if (!detail) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/ideas/${detail.id}/generate`);
      setDetail(data);
      load();
    } catch (e) {
      alert("Erreur génération: " + (e.response?.data?.detail || e.message));
    } finally { setGenerating(false); }
  };

  const suggest = async (brand) => {
    setSuggesting(brand);
    setSuggestions(null);
    try {
      const count = brand === "PPD" ? 10 : 5;
      const { data } = await api.post("/ideas/suggest", { brand, count });
      setSuggestions({ brand, ideas: data.ideas || [] });
    } catch (e) {
      alert("Erreur suggestion: " + (e.response?.data?.detail || e.message));
    } finally { setSuggesting(null); }
  };

  const confirmSuggestions = async (selectedIdxs) => {
    if (!suggestions) return;
    const chosen = suggestions.ideas.filter((_, i) => selectedIdxs.includes(i));
    await api.post("/ideas/bulk", { brand: suggestions.brand, ideas: chosen });
    setSuggestions(null);
    load();
  };

  const toggleFormTag = (t) => setFormTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  const filtered = filterTag ? list.filter((i) => (i.tags || []).includes(filterTag)) : list;

  return (
    <div className="space-y-6" data-testid="ideas-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">Stratégie LinkedIn · Directeur éditorial intégré</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Idées & calendrier</h1>
          <p className="text-sm text-gray-600 mt-1">Rythme : 2 posts PPD + 1 Franchi'Mob par semaine.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {li?.connected ? (
            <button data-testid="linkedin-connected" onClick={disconnectLinkedIn} title="Déconnecter LinkedIn" className="bg-[#0A66C2] text-white px-3 py-2 rounded text-xs font-semibold flex items-center gap-1.5">
              <Linkedin size={14}/> {li.name || "LinkedIn connecté"} <CheckCircle2 size={13}/>
            </button>
          ) : (
            <button data-testid="linkedin-connect" onClick={connectLinkedIn} className="border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white px-3 py-2 rounded text-xs font-semibold flex items-center gap-1.5 transition-colors">
              <Linkedin size={14}/> Connecter LinkedIn
            </button>
          )}
          <button data-testid="suggest-ppd" onClick={() => suggest("PPD")} disabled={suggesting} className="bg-[#D05E44] text-white px-3 py-2 rounded text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {suggesting === "PPD" ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>} Suggérer 10 idées PPD
          </button>
          <button data-testid="suggest-fmob" onClick={() => suggest("FRANCHIMOB")} disabled={suggesting} className="bg-[#1E3A8A] text-white px-3 py-2 rounded text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {suggesting === "FRANCHIMOB" ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>} Suggérer 5 idées F'Mob
          </button>
          <button data-testid="idea-new" onClick={() => { setCaptureText(""); setShow(true); }} className="bg-gray-900 text-white px-3 py-2 rounded text-xs font-semibold flex items-center gap-1.5"><Plus size={14}/> Nouvelle idée</button>
        </div>
      </div>

      {/* CALENDRIER ÉDITORIAL MENSUEL */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="editorial-calendar">
        <div className="lg:col-span-2">
          <MiniCalendar
            onIdeaDrop={async (ideaId, dayIso) => {
              await api.put(`/ideas/${ideaId}`, { scheduled_date: dayIso, status: "a_publier" });
              load();
            }}
            onIdeaClick={(id) => {
              const it = list.find((x) => x.id === id);
              if (it) setDetail(it);
            }}
          />
        </div>
        <CalendarKpisWrapper/>
      </div>

      {/* TAG FILTERS */}
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setFilterTag("")} data-testid="tag-filter-all" className={`px-3 py-1 text-xs font-semibold rounded ${!filterTag ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>Tous</button>
        {TAGS.map((t) => (
          <button key={t} data-testid={`tag-filter-${t}`} onClick={() => setFilterTag(filterTag === t ? "" : t)} className={`px-3 py-1 text-xs font-semibold rounded capitalize ${filterTag === t ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{t}</button>
        ))}
      </div>

      {/* KANBAN */}
      <div className="flex gap-3 overflow-x-auto pb-4 kanban-scroll">
        {COLS.map((c) => {
          const items = filtered.filter((i) => i.status === c.id);
          const isOver = dragOverCol === c.id;
          return (
            <div
              key={c.id}
              className={`min-w-[230px] flex-1 rounded-lg p-3 border transition-colors ${isOver ? "bg-[#C26B53]/10 border-[#C26B53]" : "bg-gray-50/60 border-gray-100"}`}
              data-testid={`ideas-col-${c.id}`}
              onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== c.id) setDragOverCol(c.id); }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverCol(null); }}
              onDrop={() => handleDrop(c.id)}
            >
              <div className="flex justify-between items-center mb-3">
                <div className="text-xs font-bold uppercase tracking-widest text-gray-600">{c.label}</div>
                <div className="text-xs font-semibold text-gray-400">{items.length}</div>
              </div>
              <div className="space-y-2">
                {items.map((i) => {
                  const isPublie = i.status === "publie";
                  const firstImg = (i.images || [])[0];
                  return (
                    <div
                      key={i.id}
                      data-testid={`idea-${i.id}`}
                      draggable
                      onDragStart={(e) => { setDragId(i.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDragId(null); setDragOverCol(null); }}
                      onClick={() => setDetail(i)}
                      className={`card-flat p-3 text-left w-full hover:shadow-md transition cursor-grab active:cursor-grabbing border-l-4 ${isPublie ? "bg-emerald-50 border-l-emerald-500" : "border-l-transparent"}`}
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <GripVertical size={12} className="text-gray-300 shrink-0"/>
                        <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isPublie}
                            onChange={() => togglePublish(i)}
                            data-testid={`idea-publish-${i.id}`}
                            className="accent-emerald-600 w-3.5 h-3.5"
                          />
                          {isPublie ? <span className="text-emerald-700 flex items-center gap-0.5"><CheckCircle2 size={11}/> Publié</span> : "Publié ?"}
                        </label>
                        <button
                          type="button"
                          onClick={(e) => deleteIdea(i.id, e)}
                          data-testid={`idea-delete-${i.id}`}
                          title="Supprimer cette idée"
                          className="ml-auto text-gray-300 hover:text-rose-600 transition-colors shrink-0"
                        >
                          <X size={14}/>
                        </button>
                      </div>
                      <div className="flex items-center gap-1 mb-1 flex-wrap">
                        <span className={`pill ${i.brand === "PPD" ? "chip-ppd" : "chip-fmob"} text-[10px]`}>{i.brand === "PPD" ? "PPD" : "F.MOB"}</span>
                        {i.potentiel > 0 && <Stars value={i.potentiel} size={10}/>}
                        {i.scheduled_date && <span className="pill bg-amber-50 text-amber-700 text-[10px]">📅 {formatDate(i.scheduled_date)}</span>}
                        {(i.images || []).length > 0 && <span className="pill bg-blue-50 text-blue-700 text-[10px] flex items-center gap-0.5"><ImageIcon size={9}/> {i.images.length}</span>}
                      </div>
                      <div className="flex gap-2">
                        {firstImg && (
                          <img
                            src={`${API}/ideas/${i.id}/images/${firstImg.id}`}
                            alt=""
                            className="w-12 h-12 rounded object-cover shrink-0 border border-gray-200"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 line-clamp-2">{i.title}</div>
                          {i.publication_type && <div className="text-[10px] text-gray-500 mt-1">{i.publication_type}</div>}
                          {i.project_nom && <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1"><Link2 size={9}/> {i.project_nom}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ========= SUGGEST MODAL ========= */}
      {suggestions && (
        <SuggestModal
          suggestions={suggestions}
          onCancel={() => setSuggestions(null)}
          onConfirm={confirmSuggestions}
        />
      )}

      {/* ========= DETAIL MODAL ========= */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="idea-detail">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`pill ${detail.brand === "PPD" ? "chip-ppd" : "chip-fmob"}`}>{detail.brand === "PPD" ? "PPD" : "Franchi'Mob"}</span>
              <span className="pill bg-gray-100 text-gray-700">{detail.status}</span>
              {detail.project_nom && <span className="pill bg-blue-50 text-blue-800 flex items-center gap-1"><Link2 size={10}/> {detail.project_nom}</span>}
              {detail.publication_type && <span className="pill bg-gray-100 text-gray-700">{detail.publication_type}</span>}
            </div>
            <h3 className="font-display text-2xl font-bold tracking-tight">{detail.title}</h3>

            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Potentiel</span>
              <Stars value={detail.potentiel || 0} onChange={(v) => setPotentiel(detail.id, v)} size={18}/>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Date de publi.</span>
                <input
                  type="date"
                  data-testid="idea-scheduled-date"
                  value={detail.scheduled_date || ""}
                  onChange={(e) => setScheduledDate(detail.id, e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                />
              </div>
            </div>

            {detail.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">{detail.tags.map((t) => <span key={t} className="pill bg-gray-100 text-gray-600">{t}</span>)}</div>
            )}
            {detail.capture && <div className="mt-3 text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100 whitespace-pre-wrap">{detail.capture}</div>}

            {/* PHOTOS — texte et/ou photo */}
            <div className="mt-4" data-testid="idea-photos">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest font-bold text-gray-500 flex items-center gap-1.5"><ImageIcon size={12}/> Photos du post</div>
                <label className="text-xs font-semibold text-[#C26B53] hover:text-[#A6452F] cursor-pointer flex items-center gap-1" data-testid="idea-add-photo">
                  {uploadingImg ? <Loader2 size={13} className="animate-spin"/> : <ImagePlus size={13}/>}
                  {uploadingImg ? "Envoi…" : "Ajouter une photo"}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploadingImg}
                    className="hidden"
                    onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }}
                  />
                </label>
              </div>
              {(detail.images || []).length === 0 ? (
                <div className="text-xs text-gray-400 italic">Aucune photo. Un post peut être texte seul, photo seule, ou les deux.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {detail.images.map((img) => (
                    <div key={img.id} className="relative group" data-testid={`idea-photo-${img.id}`}>
                      <img src={`${API}/ideas/${detail.id}/images/${img.id}`} alt={img.filename} className="w-full h-24 object-cover rounded border border-gray-200"/>
                      <button
                        onClick={() => deleteImage(img.id)}
                        data-testid={`idea-photo-del-${img.id}`}
                        className="absolute top-1 right-1 bg-white/90 hover:bg-rose-600 hover:text-white text-rose-600 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition"
                        title="Supprimer la photo"
                      >
                        <X size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button data-testid="idea-generate" onClick={generate} disabled={generating} className="mt-4 bg-[#D05E44] hover:bg-[#A6452F] text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              {generating ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
              {generating ? "Génération…" : "Générer (3 accroches + 3 angles + 2 brouillons)"}
            </button>

            {detail.accroches?.length > 0 && (<div className="mt-5"><div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">3 accroches</div><ul className="space-y-1.5">{detail.accroches.map((a, i) => <li key={`acc-${i}-${(a || "").slice(0, 24)}`} className="text-sm bg-gray-50 p-2 rounded border border-gray-100">{a}</li>)}</ul></div>)}
            {detail.angles?.length > 0 && (<div className="mt-4"><div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">3 angles éditoriaux</div><ul className="space-y-1.5">{detail.angles.map((a, i) => <li key={`ang-${i}-${(a || "").slice(0, 24)}`} className="text-sm bg-gray-50 p-2 rounded border border-gray-100">{a}</li>)}</ul></div>)}
            {detail.brouillon && (<div className="mt-4"><div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Brouillon principal</div><div className="text-sm whitespace-pre-wrap bg-amber-50 border border-amber-100 p-4 rounded">{detail.brouillon}</div></div>)}
            {detail.brouillon_court && (<div className="mt-4"><div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Version courte (mobile)</div><div className="text-sm whitespace-pre-wrap bg-blue-50 border border-blue-100 p-4 rounded">{detail.brouillon_court}</div></div>)}

            {/* PUBLICATION LINKEDIN */}
            <div className="mt-5 border-t border-gray-100 pt-4" data-testid="linkedin-publish-block">
              <div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2 flex items-center gap-1.5"><Linkedin size={13} className="text-[#0A66C2]"/> Publier sur LinkedIn</div>

              {detail.linkedin_status === "published" ? (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded p-3 flex items-center gap-2" data-testid="linkedin-published">
                  <CheckCircle2 size={15}/> Publié sur LinkedIn
                  {detail.linkedin_post_urn && <a href={`https://www.linkedin.com/feed/update/${detail.linkedin_post_urn}/`} target="_blank" rel="noreferrer" className="underline ml-1">Voir le post</a>}
                </div>
              ) : (
                <>
                  {detail.linkedin_status === "scheduled" && (
                    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded p-2.5 mb-2 flex items-center justify-between gap-2" data-testid="linkedin-scheduled">
                      <span className="flex items-center gap-1.5"><Clock size={14}/> Programmé pour {formatDate(detail.linkedin_scheduled_at)} {String(detail.linkedin_scheduled_at).slice(11, 16)}</span>
                      <button onClick={cancelSchedule} className="text-xs underline hover:text-amber-900">Annuler</button>
                    </div>
                  )}
                  {detail.linkedin_status === "failed" && detail.linkedin_error && (
                    <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded p-2.5 mb-2 flex items-start gap-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0"/> {detail.linkedin_error}</div>
                  )}

                  {/* APERÇU FIL LINKEDIN — mise à jour en direct */}
                  <div className="mb-3" data-testid="linkedin-preview">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-1.5">Aperçu du rendu</div>
                    <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden max-w-md">
                      <div className="p-3">
                        <div className="flex items-center gap-2.5">
                          {li?.picture ? (
                            <img src={li.picture} alt="" className="w-11 h-11 rounded-full object-cover"/>
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-[#0A66C2] text-white flex items-center justify-center font-bold text-sm">{(li?.name || "PP").slice(0, 2).toUpperCase()}</div>
                          )}
                          <div className="leading-tight min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">{li?.name || "Votre profil LinkedIn"}</div>
                            <div className="text-[11px] text-gray-500 truncate">Pourquoi Pas Design · Architecture & franchise</div>
                            <div className="text-[11px] text-gray-500 flex items-center gap-1">{detail.linkedin_status === "scheduled" ? "Programmé" : "Maintenant"} · <Globe size={10}/></div>
                          </div>
                        </div>
                        {liText ? (
                          <div className="text-sm text-gray-800 whitespace-pre-wrap mt-2.5 leading-snug break-words">
                            {(() => {
                              // Collapse "façon LinkedIn" : ~3 lignes ou 210 caractères, en respectant les sauts de ligne
                              const lines = liText.split("\n");
                              let collapsed = lines.slice(0, 3).join("\n");
                              if (collapsed.length > 210) collapsed = collapsed.slice(0, 210);
                              const truncated = collapsed.length < liText.length;
                              if (!truncated || liPreviewExpanded) {
                                return (
                                  <>
                                    {liText}
                                    {truncated && (
                                      <> <button type="button" onClick={() => setLiPreviewExpanded(false)} className="text-gray-500 font-medium hover:text-[#0A66C2]">voir moins</button></>
                                    )}
                                  </>
                                );
                              }
                              return (
                                <>{collapsed}… <button type="button" onClick={() => setLiPreviewExpanded(true)} className="text-gray-500 font-medium hover:text-[#0A66C2]">voir plus</button></>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400 italic mt-2.5">Écris le texte du post ci-dessous…</div>
                        )}
                      </div>
                      {(detail.images || []).length > 0 && (
                        <div className={`grid gap-0.5 ${detail.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                          {detail.images.slice(0, 4).map((img, idx) => (
                            <div key={img.id} className="relative">
                              <img src={`${API}/ideas/${detail.id}/images/${img.id}`} alt="" className={`w-full object-cover ${detail.images.length === 1 ? "max-h-72" : "h-32"}`}/>
                              {idx === 3 && detail.images.length > 4 && (
                                <div className="absolute inset-0 bg-black/50 text-white flex items-center justify-center text-lg font-semibold">+{detail.images.length - 4}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-around border-t border-gray-100 px-2 py-1.5 text-[11px] text-gray-500 font-medium">
                        <span className="flex items-center gap-1"><ThumbsUp size={13}/> J'aime</span>
                        <span className="flex items-center gap-1"><MessageSquare size={13}/> Commenter</span>
                        <span className="flex items-center gap-1"><Repeat2 size={13}/> Republier</span>
                        <span className="flex items-center gap-1"><Send size={13}/> Envoyer</span>
                      </div>
                    </div>
                  </div>

                  <textarea
                    data-testid="linkedin-text"
                    value={liText}
                    onChange={(e) => setLiText(e.target.value)}
                    rows={5}
                    placeholder="Texte du post à publier…"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
                  />
                  {(detail.images || []).length > 0 && (
                    <div className="text-[11px] text-gray-500 mb-2">{detail.images.length} photo(s) jointe(s) seront incluses dans le post.</div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      data-testid="linkedin-publish-now"
                      onClick={() => publishToLinkedIn(false)}
                      disabled={publishing}
                      className="bg-[#0A66C2] hover:bg-[#084d92] text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                    >
                      {publishing ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Publier maintenant
                    </button>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <input
                        type="datetime-local"
                        data-testid="linkedin-schedule-at"
                        value={scheduleAt}
                        onChange={(e) => setScheduleAt(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1.5 text-xs"
                      />
                      <button
                        data-testid="linkedin-schedule-btn"
                        onClick={() => publishToLinkedIn(true)}
                        disabled={publishing || !scheduleAt}
                        className="border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white px-3 py-2 rounded text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40 transition-colors"
                      >
                        <Clock size={14}/> Programmer
                      </button>
                    </div>
                  </div>
                  {!li?.connected && (
                    <div className="text-xs text-amber-700 mt-2 flex items-center gap-1"><AlertTriangle size={12}/> Compte LinkedIn non connecté — clique sur « Connecter LinkedIn » en haut de la page.</div>
                  )}
                </>
              )}
            </div>

            <div className="mt-5 flex flex-wrap gap-1.5">
              {COLS.filter((c) => c.id !== detail.status).map((c) => (
                <button key={c.id} onClick={() => setStatus(detail.id, c.id)} className="text-xs px-2 py-1 border rounded hover:bg-gray-50">→ {c.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CREATE IDEA */}
      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="idea-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouvelle idée</h3>
            <div className="space-y-3 text-sm">
              <input name="title" required placeholder="Titre / accroche brute" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <div>
                <textarea name="capture" value={captureText} onChange={(e) => setCaptureText(e.target.value)} placeholder="Capture rapide : note vocale, idée, anecdote…" rows={3} className="w-full border border-gray-300 rounded px-3 py-2"/>
                <label className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C26B53] hover:text-[#A6452F] cursor-pointer" data-testid="idea-image-prompt">
                  {analyzingImg ? <Loader2 size={13} className="animate-spin"/> : <ImagePlus size={13}/>}
                  {analyzingImg ? "Analyse de l'image…" : "Générer l'idée depuis une image"}
                  <input type="file" accept="image/*" disabled={analyzingImg} className="hidden" data-testid="idea-image-input"
                    onChange={(e) => { analyzeImageForPrompt(e.target.files?.[0]); e.target.value = ""; }}/>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select name="brand" id="form-brand" className="border border-gray-300 rounded px-3 py-2"><option value="PPD">PPD</option><option value="FRANCHIMOB">Franchi'Mob</option></select>
                <input name="publication_type" placeholder="Type publication" className="border border-gray-300 rounded px-3 py-2"/>
              </div>
              <select name="project_id" className="w-full border border-gray-300 rounded px-3 py-2">
                <option value="">— Projet associé (optionnel) —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.nom} · {p.client}</option>)}
              </select>
              <input name="scheduled_date" type="date" className="w-full border border-gray-300 rounded px-3 py-2" placeholder="Date de publication"/>
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1.5">Mots-clés</div>
                <div className="flex flex-wrap gap-1.5">
                  {TAGS.map((t) => (
                    <button key={t} type="button" data-testid={`form-tag-${t}`} onClick={() => toggleFormTag(t)} className={`px-2.5 py-1 text-xs rounded capitalize ${formTags.includes(t) ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Potentiel</span>
                <Stars value={formPotentiel} onChange={setFormPotentiel} size={18}/>
                <span className="text-xs text-gray-500">{formPotentiel === 3 ? "Excellent" : formPotentiel === 2 ? "Moyen" : formPotentiel === 1 ? "Faible" : "—"}</span>
              </div>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border rounded">Annuler</button>
              <ActionButton type="button" onClick={create} data-testid="idea-submit" successLabel="Validé" className="px-4 py-2 text-sm bg-[#D05E44] text-white rounded font-semibold">Valider</ActionButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SuggestModal({ suggestions, onCancel, onConfirm }) {
  const [selected, setSelected] = useState(suggestions.ideas.map((_, i) => i));
  const toggle = (i) => setSelected((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="suggest-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-2xl font-bold">Suggestions {suggestions.brand === "PPD" ? "Pourquoi Pas Design" : "Franchi'Mob"}</h3>
            <p className="text-sm text-gray-500">Classées par pertinence. Décoche celles que tu ne veux pas garder.</p>
          </div>
          <div className="text-sm font-mono">{selected.length}/{suggestions.ideas.length}</div>
        </div>
        {suggestions.ideas.length === 0 ? (
          <div className="text-sm text-gray-400 italic py-6 text-center">Aucune suggestion produite. Réessayez plus tard.</div>
        ) : (
          <div className="space-y-2">
            {suggestions.ideas.map((idea, i) => (
              <label key={`sug-${i}-${(idea.title || "").slice(0, 30)}`} data-testid={`sug-${i}`} className={`block p-3 border rounded cursor-pointer transition ${selected.includes(i) ? "border-gray-900 bg-gray-50" : "border-gray-200 opacity-60"}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={selected.includes(i)} onChange={() => toggle(i)} className="mt-1"/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{idea.title}</span>
                      {idea.publication_type && <span className="pill bg-gray-100 text-gray-700 text-[10px]">{idea.publication_type}</span>}
                      {idea.potentiel > 0 && <Stars value={idea.potentiel} size={11}/>}
                    </div>
                    {idea.capture && <div className="text-xs text-gray-600">{idea.capture}</div>}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm border rounded">Annuler</button>
          <button data-testid="suggest-confirm" onClick={() => onConfirm(selected)} disabled={selected.length === 0} className="px-4 py-2 text-sm bg-gray-900 text-white rounded font-semibold disabled:opacity-50">
            Ajouter {selected.length} idée{selected.length > 1 ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
